import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
} from '@ant-design/icons';
import { Button, Col, Popconfirm, Row, TableColumnType } from 'antd';
import { LocationOutput, RegionOutput } from 'gql/types';
import { ControlledInput, FormItem } from 'modules/common/components';
import { IconTooltip } from 'modules/common/components/IconTooltip';
import { Control, FormState } from 'react-hook-form';
import { getTranslation } from 'utils/getTranslation';

import { IUpdateRegionsFormSchema } from './schema';
import styles from './styles.module.scss';

const t = getTranslation('user.LocationsTable');

type FormErrorsType = FormState<IUpdateRegionsFormSchema>['errors'];

type ParamType = {
  editableKey?: string;
  setEditableKey?: (
    editableKey?: string,
    locationName?: string,
    idexxAccountNumber?: string,
    zoetisAccountNumber?: string
  ) => void;
  onSaveChanges?: () => void;
  onDelete?: (locationId: string) => void;
  control: Control<IUpdateRegionsFormSchema, any>;
  savingIsDisabled?: boolean;
  isAllowedToEdit?: boolean;
  isAllowedToDelete?: boolean;
  errors: FormErrorsType;
  isMobile: boolean;
};

export const getColumns = (
  params: ParamType
): TableColumnType<RegionOutput>[] => {
  const {
    control,
    setEditableKey,
    editableKey,
    onDelete,
    onSaveChanges,
    savingIsDisabled,
    isAllowedToDelete,
    isAllowedToEdit,
    errors,
  } = params;

  const isEditing = (regionId: string) => editableKey === regionId;
  const getIdexxErrorMessage = (errors: FormErrorsType) => {
    return errors.idexxAccountNumber?.message;
  };
  const getZoetisErrorMessage = (errors: FormErrorsType) => {
    return errors.zoetisAccountNumber?.message;
  };
  const getNameErrorMessage = (errors: FormErrorsType) => {
    return errors.name?.message;
  };

  return [
    {
      width: '25%',
      title: t('name'),
      render: ({ id, name }: LocationOutput) => {
        const errorMessage = getNameErrorMessage(errors);
        const validateStatus = errorMessage && 'error';
        return !isEditing(id) ? (
          <span title={name} className={styles.locationName}>
            {name}
          </span>
        ) : (
          <FormItem validateStatus={validateStatus} className={styles.formItem}>
            <ControlledInput
              placeholder={t('enterRegionName')}
              suffix={
                <IconTooltip
                  text={errorMessage}
                  hidden={!errorMessage}
                  type="error"
                />
              }
              controlled={{ control, name: 'name', defaultValue: name }}
            />
          </FormItem>
        );
      },
    },
    {
      width: '25%',
      title: t('idexxAccountNumber'),
      render: ({ id, idexxAccountNumber }: RegionOutput) => {
        const errorMessage = getIdexxErrorMessage(errors);
        const validateStatus = errorMessage && 'error';
        return !isEditing(id) ? (
          <span
            title={idexxAccountNumber || ''}
            className={styles.locationName}
          >
            {idexxAccountNumber}
          </span>
        ) : (
          <FormItem validateStatus={validateStatus} className={styles.formItem}>
            <ControlledInput
              placeholder={t('enterIdexxAccountNumber')}
              suffix={
                <IconTooltip
                  text={errorMessage}
                  hidden={!errorMessage}
                  type="error"
                />
              }
              controlled={{
                control,
                name: 'idexxAccountNumber',
                defaultValue: idexxAccountNumber,
              }}
            />
          </FormItem>
        );
      },
    },
    {
      width: '25%',
      title: t('zoetisAccountNumber'),
      render: ({ id, zoetisAccountNumber }: RegionOutput) => {
        const errorMessage = getZoetisErrorMessage(errors);
        const validateStatus = errorMessage && 'error';
        return !isEditing(id) ? (
          <span
            title={zoetisAccountNumber || ''}
            className={styles.locationName}
          >
            {zoetisAccountNumber}
          </span>
        ) : (
          <FormItem validateStatus={validateStatus} className={styles.formItem}>
            <ControlledInput
              placeholder={t('enterZoetisAccountNumber')}
              suffix={
                <IconTooltip
                  text={errorMessage}
                  hidden={!errorMessage}
                  type="error"
                />
              }
              controlled={{
                control,
                name: 'zoetisAccountNumber',
                defaultValue: zoetisAccountNumber,
              }}
            />
          </FormItem>
        );
      },
    },
    {
      width: '25%',
      render: (regionOutput: RegionOutput) => {
        return isEditing(regionOutput.id) ? (
          <Row gutter={15}>
            <Col span={12}>
              <Popconfirm
                overlayClassName={styles.popConfirm}
                getPopupContainer={(triggerNode) =>
                  triggerNode.parentNode as HTMLElement
                }
                title={t('sureToSave')}
                onConfirm={onSaveChanges}
                disabled={savingIsDisabled}
              >
                <Button type="text" size="small" disabled={savingIsDisabled}>
                  <CheckOutlined />
                </Button>
              </Popconfirm>
            </Col>
            <Col span={12}>
              <Button
                onClick={() => setEditableKey?.(undefined)}
                type="text"
                size="small"
              >
                <CloseOutlined />
              </Button>
            </Col>
          </Row>
        ) : (
          <Row align="middle">
            <Col span={12}>
              <Button
                onClick={() =>
                  setEditableKey?.(
                    regionOutput.id,
                    regionOutput.name,
                    regionOutput.idexxAccountNumber as string,
                    regionOutput.zoetisAccountNumber as string
                  )
                }
                type="link"
                size="small"
                disabled={!isAllowedToEdit}
              >
                <EditOutlined />
              </Button>
            </Col>
            <Col span={12}>
              <Popconfirm
                overlayClassName={styles.popConfirm}
                getPopupContainer={(triggerNode) =>
                  triggerNode.parentNode as HTMLElement
                }
                title={t('sureToDelete')}
                onConfirm={() => onDelete?.(regionOutput.id)}
                disabled={!isAllowedToDelete}
              >
                <Button danger type="link" disabled={!isAllowedToDelete}>
                  <DeleteOutlined />
                </Button>
              </Popconfirm>
            </Col>
          </Row>
        );
      },
    },
  ];
};
